/**
 * Dedicated offscreen scene-signal capture for the CPU inking pipeline.
 *
 * Unlike the previous screen-canvas scrape, this renders explicit passes for:
 * - lit color     → luminance used for light-aware stroke styling
 * - object IDs    → stable object/silhouette boundaries
 * - view normals  → topology-aware crease detection
 * - linear depth  → real depth discontinuities and distance-aware styling
 *
 * The capture is self-contained and does not depend on the old realtime
 * inking pipeline or its prepass layers.
 */

import {
  cameraFar,
  cameraNear,
  clamp,
  float,
  positionView,
  vec4,
  viewZToOrthographicDepth,
} from "three/tsl";
import * as THREE from "three/webgpu";
import { RendererUtils } from "three/webgpu";
import { useStore } from "../../../app/store.ts";
import { isObjectVisibleInWorkspace } from "../../../app/visibilityPolicy.ts";
import { resolveAutoSmoothAngle } from "../../../modeling/ops/normals.ts";
import {
  disposeReusableGBufferPool,
  listReadbackTiles,
  type ReadbackTile,
  recycleGBuffer,
  takeReusableGBuffer,
} from "./gbufferPool.ts";
import type { GBufferData } from "./types.ts";

interface CaptureObjectInfo {
  key: string;
  packedColor: number;
  resolvedAutoSmoothAngle: number;
}

interface CaptureResources {
  colorRenderTarget: THREE.RenderTarget;
  depthRenderTarget: THREE.RenderTarget;
  size: THREE.Vector2;
  savedState: any;
  objectIdInfoByPackedColor: Map<number, CaptureObjectInfo>;
}

interface HiddenSceneState {
  hidden: THREE.Object3D[];
  captureMeshes: THREE.Mesh[];
}

interface CaptureVisibilityState {
  visibility: ReadonlyMap<string, boolean>;
  renderVisibility: ReadonlyMap<string, boolean>;
}

interface MaterialSwapState {
  mesh: THREE.Mesh;
  material: THREE.Material | THREE.Material[];
}

type ReadbackArray = Uint8Array | Float32Array;

let resources: CaptureResources | null = null;

export function syncReadbackRenderTargetSize(
  renderTarget: THREE.RenderTarget,
  width: number,
  height: number,
): void {
  if (renderTarget.width !== width || renderTarget.height !== height) {
    renderTarget.setSize(width, height);
  }
  renderTarget.viewport.set(0, 0, width, height);
  renderTarget.scissor.set(0, 0, width, height);
  renderTarget.scissorTest = false;
}

function getResources(width: number, height: number): CaptureResources {
  if (!resources) {
    const colorRenderTarget = new THREE.RenderTarget(width, height, {
      format: THREE.RGBAFormat,
      type: THREE.UnsignedByteType,
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      generateMipmaps: false,
      depthBuffer: true,
    });
    colorRenderTarget.texture.name = "InkingCpuReadback";
    syncReadbackRenderTargetSize(colorRenderTarget, width, height);

    const depthRenderTarget = new THREE.RenderTarget(width, height, {
      format: THREE.RGBAFormat,
      type: THREE.FloatType,
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      generateMipmaps: false,
      depthBuffer: true,
    });
    depthRenderTarget.texture.name = "InkingCpuDepthReadback";
    syncReadbackRenderTargetSize(depthRenderTarget, width, height);

    resources = {
      colorRenderTarget,
      depthRenderTarget,
      size: new THREE.Vector2(width, height),
      savedState: undefined,
      objectIdInfoByPackedColor: new Map(),
    };
  }

  if (resources.size.x !== width || resources.size.y !== height) {
    resources.size.set(width, height);
  }
  syncReadbackRenderTargetSize(resources.colorRenderTarget, width, height);
  syncReadbackRenderTargetSize(resources.depthRenderTarget, width, height);

  return resources;
}

function hasExcludedAncestor(obj: THREE.Object3D): boolean {
  for (let node: THREE.Object3D | null = obj; node; node = node.parent) {
    if (node.userData?.excludeFromExport) return true;
  }
  return false;
}

function isHiddenMaterial(
  material: THREE.Material | THREE.Material[] | null | undefined,
): boolean {
  if (!material) return true;
  if (Array.isArray(material)) {
    return (
      material.length === 0 || material.every((m) => !m || m.visible === false)
    );
  }
  return material.visible === false;
}

function isGridMaterial(
  material: THREE.Material | THREE.Material[] | null | undefined,
): boolean {
  if (!material) return false;
  if (Array.isArray(material))
    return material.some((m) => m?.type === "GridMaterial");
  return material.type === "GridMaterial";
}

function resolveCaptureVisibilityKeys(obj: THREE.Object3D): string[] {
  const keys: string[] = [];
  for (let node: THREE.Object3D | null = obj; node; node = node.parent) {
    const objectId = node.userData?.poserefObjectId;
    if (typeof objectId === "string" && objectId.length > 0) {
      keys.push(objectId);
      break;
    }
  }
  if (obj.name) keys.push(obj.name);
  return keys;
}

function prepareSceneForCapture(
  scene: THREE.Scene,
  storeState: CaptureVisibilityState,
): HiddenSceneState {
  const hidden: THREE.Object3D[] = [];
  const captureMeshes: THREE.Mesh[] = [];

  scene.traverse((obj) => {
    if (!obj.visible) return;
    const anyObj = obj as any;

    if (hasExcludedAncestor(obj)) {
      obj.visible = false;
      hidden.push(obj);
      return;
    }

    if (
      anyObj.isLine ||
      anyObj.isLineSegments ||
      anyObj.isLine2 ||
      anyObj.isLineSegments2 ||
      anyObj.isSprite ||
      anyObj.isPoints
    ) {
      obj.visible = false;
      hidden.push(obj);
      return;
    }

    if (!anyObj.isMesh) return;

    const mesh = obj as THREE.Mesh;
    if (
      !isObjectVisibleInWorkspace({
        workspaceMode: "inking",
        visibility: storeState.visibility,
        renderVisibility: storeState.renderVisibility,
        keys: resolveCaptureVisibilityKeys(mesh),
      })
    ) {
      mesh.visible = false;
      hidden.push(mesh);
      return;
    }
    if (
      isHiddenMaterial((mesh as any).material) ||
      isGridMaterial((mesh as any).material)
    ) {
      mesh.visible = false;
      hidden.push(mesh);
      return;
    }

    captureMeshes.push(mesh);
  });

  return { hidden, captureMeshes };
}

function restoreSceneVisibility(hidden: THREE.Object3D[]): void {
  for (const obj of hidden) obj.visible = true;
}

function hashKeyToPackedColor(key: string): number {
  let hash = 2166136261;
  for (let i = 0; i < key.length; i++) {
    hash ^= key.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }

  return ((hash >>> 0) & 0x00ffffff) | 0x202020;
}

function packedColorToColor(rgb: number): THREE.Color {
  const color = new THREE.Color(rgb);
  if (color.r + color.g + color.b < 0.35) {
    color.r += 0.2;
    color.g += 0.2;
    color.b += 0.2;
  }
  return color;
}

function getReadbackPackedColors(color: THREE.Color): number[] {
  const linearPacked = color.getHex(THREE.LinearSRGBColorSpace);
  const srgbPacked = color.getHex(THREE.SRGBColorSpace);
  return linearPacked === srgbPacked
    ? [linearPacked]
    : [linearPacked, srgbPacked];
}

function resolveCaptureObjectInfo(mesh: THREE.Mesh): CaptureObjectInfo {
  const sceneObjects = useStore.getState().sceneObjects;
  let highestTagged: THREE.Object3D | null = null;

  for (let node: THREE.Object3D | null = mesh; node; node = node.parent) {
    const objectId = node.userData?.poserefObjectId;
    if (typeof objectId === "string" && objectId.length > 0) {
      const object = sceneObjects.get(objectId);
      return {
        key: `object:${objectId}`,
        packedColor: hashKeyToPackedColor(`object:${objectId}`),
        resolvedAutoSmoothAngle: object
          ? resolveAutoSmoothAngle(object.shading, object.autoSmoothAngle)
          : 180,
      };
    }
    if (node.userData?.poserefInkingInclude) {
      highestTagged = node;
    }
  }

  if (highestTagged) {
    const category = String(
      highestTagged.userData?.poserefInkingInclude ?? "scene",
    );
    const name = highestTagged.name || highestTagged.uuid;
    const key = `scene:${category}:${name}`;
    return {
      key,
      packedColor: hashKeyToPackedColor(key),
      resolvedAutoSmoothAngle: 180,
    };
  }

  const key = `mesh:${mesh.uuid}`;
  return {
    key,
    packedColor: hashKeyToPackedColor(key),
    resolvedAutoSmoothAngle: 180,
  };
}

const DEPTH_COLOR_NODE = vec4(
  clamp(
    viewZToOrthographicDepth(positionView.z, cameraNear, cameraFar),
    float(0),
    float(1),
  ),
  float(0),
  float(0),
  float(1),
);

function makeObjectIdMaterial(
  mesh: THREE.Mesh,
  res: CaptureResources,
): THREE.MeshBasicNodeMaterial {
  const info = resolveCaptureObjectInfo(mesh);
  const mat = new THREE.MeshBasicNodeMaterial({
    color: packedColorToColor(info.packedColor),
    depthTest: true,
    depthWrite: true,
    side: THREE.DoubleSide,
    toneMapped: false,
  });
  mat.blending = THREE.NoBlending;
  mat.name = `CpuInkingObjectId:${info.key}`;
  for (const packed of getReadbackPackedColors(mat.color)) {
    res.objectIdInfoByPackedColor.set(packed, info);
  }
  return mat;
}

function makeNormalMaterial(): THREE.MeshNormalNodeMaterial {
  const mat = new THREE.MeshNormalNodeMaterial({ side: THREE.DoubleSide });
  mat.blending = THREE.NoBlending;
  mat.toneMapped = false;
  return mat;
}

function makeDepthMaterial(): THREE.MeshBasicNodeMaterial {
  const mat = new THREE.MeshBasicNodeMaterial({ side: THREE.DoubleSide });
  mat.blending = THREE.NoBlending;
  mat.toneMapped = false;
  mat.colorNode = DEPTH_COLOR_NODE;
  return mat;
}

/**
 * Copy the mesh material's positionNode + setupPosition override onto the
 * capture material. Mannequin body materials patch these to read skinned
 * positions from a GPU compute buffer and skip SkinningNode/morphReference;
 * a fresh capture material without this patch fails WebGPU pipeline compile
 * on SkinnedMesh targets ("THREE.Vertex module is not valid") and renders
 * nothing. Regular Meshes don't set positionNode, so they fall through to
 * the default setup and render normally.
 */
function inheritPositionTransform(
  captureMat: THREE.Material,
  meshMat: THREE.Material | THREE.Material[] | null | undefined,
): void {
  const src = Array.isArray(meshMat) ? meshMat[0] : meshMat;
  if (!src) return;
  const srcAny = src as any;
  if (srcAny.positionNode != null) {
    (captureMat as any).positionNode = srcAny.positionNode;
    if (typeof srcAny.setupPosition === "function") {
      (captureMat as any).setupPosition = srcAny.setupPosition;
    }
  }
}

type MaterialFactory = (mesh: THREE.Mesh) => THREE.Material;

async function renderCapturePass(
  renderer: THREE.WebGPURenderer,
  scene: THREE.Scene,
  camera: THREE.Camera,
  renderTarget: THREE.RenderTarget,
  clearAlpha: number,
  captureMeshes: THREE.Mesh[],
  factory: MaterialFactory,
): Promise<void> {
  const swapped: MaterialSwapState[] = [];
  const created: THREE.Material[] = [];

  for (const mesh of captureMeshes) {
    const mat = factory(mesh);
    inheritPositionTransform(mat, (mesh as any).material);
    created.push(mat);
    swapped.push({ mesh, material: (mesh as any).material });
    (mesh as any).material = mat;
  }

  try {
    await renderSceneToTarget(
      renderer,
      scene,
      camera,
      renderTarget,
      0x000000,
      clearAlpha,
    );
  } finally {
    for (const entry of swapped) (entry.mesh as any).material = entry.material;
    for (const mat of created) mat.dispose();
  }
}

function trimPaddedReadbackRows<T extends ReadbackArray>(
  source: T,
  width: number,
  height: number,
  componentsPerPixel: number,
): T {
  const packedRowElements = width * componentsPerPixel;
  const bytesPerElement = source.BYTES_PER_ELEMENT;
  const paddedRowBytes =
    Math.ceil((packedRowElements * bytesPerElement) / 256) * 256;
  const paddedRowElements = paddedRowBytes / bytesPerElement;
  const packedLength = packedRowElements * height;

  if (source.length === packedLength) return source;

  const output = new (source.constructor as new (length: number) => T)(
    packedLength,
  );
  for (let y = 0; y < height; y++) {
    const srcOffset = y * paddedRowElements;
    const dstOffset = y * packedRowElements;
    output.set(
      source.subarray(srcOffset, srcOffset + packedRowElements),
      dstOffset,
    );
  }

  return output;
}

async function renderSceneToTarget(
  renderer: THREE.WebGPURenderer,
  scene: THREE.Scene,
  camera: THREE.Camera,
  renderTarget: THREE.RenderTarget,
  clearColor: THREE.ColorRepresentation,
  clearAlpha: number,
): Promise<void> {
  syncReadbackRenderTargetSize(
    renderTarget,
    renderTarget.width,
    renderTarget.height,
  );
  renderer.setScissorTest(false);
  renderer.setRenderTarget(renderTarget);
  renderer.setClearColor(clearColor, clearAlpha);
  renderer.clear(true, true, false);

  const maybeRenderAsync = (renderer as any).renderAsync;
  if (typeof maybeRenderAsync === "function") {
    await maybeRenderAsync.call(renderer, scene, camera);
  } else {
    renderer.render(scene, camera);
  }
}

async function readRenderTargetTile(
  renderer: THREE.WebGPURenderer,
  renderTarget: THREE.RenderTarget,
  tile: ReadbackTile,
): Promise<Uint8Array | Float32Array> {
  renderer.setRenderTarget(renderTarget);
  const pixelBuffer = await renderer.readRenderTargetPixelsAsync(
    renderTarget,
    tile.x,
    tile.y,
    tile.width,
    tile.height,
  );

  if (renderTarget.texture.type === THREE.FloatType) {
    const source =
      pixelBuffer instanceof Float32Array
        ? pixelBuffer
        : new Float32Array(pixelBuffer as ArrayLike<number>);
    return trimPaddedReadbackRows(source, tile.width, tile.height, 4);
  }

  const source =
    pixelBuffer instanceof Uint8Array
      ? pixelBuffer
      : new Uint8Array(pixelBuffer as ArrayLike<number>);
  return trimPaddedReadbackRows(source, tile.width, tile.height, 4);
}

function unpackRgbToNormal(
  r: number,
  g: number,
  b: number,
): [number, number, number] {
  const nx = r * 2 - 1;
  const ny = g * 2 - 1;
  const nz = b * 2 - 1;
  const lenSq = nx * nx + ny * ny + nz * nz;
  if (lenSq <= 1e-8) return [0, 0, 0];
  const invLen = 1 / Math.sqrt(lenSq);
  return [nx * invLen, ny * invLen, nz * invLen];
}

function normalizedDepthToLinear(
  camera: THREE.Camera,
  depth01: number,
): number {
  const near = (camera as any).near;
  const far = (camera as any).far;
  if (typeof near !== "number" || typeof far !== "number") return depth01;

  const clamped = Math.max(0, Math.min(1, depth01));
  return near + clamped * (far - near);
}

interface ObjectSignalDecodeState {
  colorToId: Map<number, number>;
  nextId: number;
}

function decodeObjectSignalsTile(
  bytes: Uint8Array,
  tile: ReadbackTile,
  fullWidth: number,
  res: CaptureResources,
  objectIds: Uint32Array,
  resolvedAutoSmoothAngles: Float32Array,
  state: ObjectSignalDecodeState,
): number {
  let geometryCount = 0;
  for (let row = 0; row < tile.height; row++) {
    const tileRowOffset = row * tile.width;
    const dstRowOffset = (tile.y + row) * fullWidth + tile.x;
    for (let col = 0; col < tile.width; col++) {
      const tileIndex = tileRowOffset + col;
      const bi = tileIndex * 4;
      const r = bytes[bi];
      const g = bytes[bi + 1];
      const b = bytes[bi + 2];
      if (r === 0 && g === 0 && b === 0) continue;

      const packed = (r << 16) | (g << 8) | b;
      const info = res.objectIdInfoByPackedColor.get(packed);
      if (!info) continue;

      let id = state.colorToId.get(packed);
      if (!id) {
        id = state.nextId++;
        state.colorToId.set(packed, id);
      }

      const dstIndex = dstRowOffset + col;
      objectIds[dstIndex] = id;
      resolvedAutoSmoothAngles[dstIndex] = info.resolvedAutoSmoothAngle;
      geometryCount++;
    }
  }
  return geometryCount;
}

/**
 * Render the scene into explicit CPU-readable signals for offline inking.
 */
export async function readbackGBuffer(
  renderer: THREE.WebGPURenderer,
  scene: THREE.Scene,
  camera: THREE.Camera,
  width: number,
  height: number,
): Promise<GBufferData> {
  const pixelCount = width * height;
  const res = getResources(width, height);
  const gbuffer = takeReusableGBuffer(width, height);
  const storeState = useStore.getState();
  const hiddenState = prepareSceneForCapture(scene, {
    visibility: storeState.visibility,
    renderVisibility: storeState.renderVisibility,
  });
  // Keep each capture pass as a single image readback. Tiled WebGPU readbacks
  // can produce hard 1024px discontinuities on some browser/device paths,
  // which the stroke tracer then turns into apparent inking clips.
  const tiles = listReadbackTiles(width, height, Math.max(width, height));
  let returned = false;

  const savedBg = scene.background;
  scene.background = null;

  res.objectIdInfoByPackedColor.clear();

  try {
    res.savedState = RendererUtils.resetRendererState(
      renderer as any,
      res.savedState,
    );

    gbuffer.depth.fill(0);
    gbuffer.normals.fill(0);
    gbuffer.objectIds.fill(0);
    gbuffer.luminance.fill(0);
    gbuffer.resolvedAutoSmoothAngles.fill(0);

    await renderSceneToTarget(
      renderer,
      scene,
      camera,
      res.colorRenderTarget,
      0x000000,
      1,
    );
    for (const tile of tiles) {
      const colorBytes = (await readRenderTargetTile(
        renderer,
        res.colorRenderTarget,
        tile,
      )) as Uint8Array;
      for (let row = 0; row < tile.height; row++) {
        const tileRowOffset = row * tile.width;
        const dstRowOffset = (tile.y + row) * width + tile.x;
        for (let col = 0; col < tile.width; col++) {
          const tileIndex = tileRowOffset + col;
          const bi = tileIndex * 4;
          const cr = colorBytes[bi] / 255;
          const cg = colorBytes[bi + 1] / 255;
          const cb = colorBytes[bi + 2] / 255;
          gbuffer.luminance[dstRowOffset + col] =
            cr * 0.2126 + cg * 0.7152 + cb * 0.0722;
        }
      }
    }

    await renderCapturePass(
      renderer,
      scene,
      camera,
      res.colorRenderTarget,
      0,
      hiddenState.captureMeshes,
      (mesh) => makeObjectIdMaterial(mesh, res),
    );
    let geometryCount = 0;
    const objectDecodeState: ObjectSignalDecodeState = {
      colorToId: new Map(),
      nextId: 1,
    };
    for (const tile of tiles) {
      const objectIdBytes = (await readRenderTargetTile(
        renderer,
        res.colorRenderTarget,
        tile,
      )) as Uint8Array;
      geometryCount += decodeObjectSignalsTile(
        objectIdBytes,
        tile,
        width,
        res,
        gbuffer.objectIds,
        gbuffer.resolvedAutoSmoothAngles,
        objectDecodeState,
      );
    }

    await renderCapturePass(
      renderer,
      scene,
      camera,
      res.colorRenderTarget,
      1,
      hiddenState.captureMeshes,
      makeNormalMaterial,
    );
    for (const tile of tiles) {
      const normalBytes = (await readRenderTargetTile(
        renderer,
        res.colorRenderTarget,
        tile,
      )) as Uint8Array;
      for (let row = 0; row < tile.height; row++) {
        const tileRowOffset = row * tile.width;
        const dstRowOffset = (tile.y + row) * width + tile.x;
        for (let col = 0; col < tile.width; col++) {
          const tileIndex = tileRowOffset + col;
          const dstIndex = dstRowOffset + col;
          if (gbuffer.objectIds[dstIndex] === 0) continue;
          const bi = tileIndex * 4;
          const nr = normalBytes[bi] / 255;
          const ng = normalBytes[bi + 1] / 255;
          const nb = normalBytes[bi + 2] / 255;
          const [nx, ny, nz] = unpackRgbToNormal(nr, ng, nb);
          const ni = dstIndex * 3;
          gbuffer.normals[ni] = nx;
          gbuffer.normals[ni + 1] = ny;
          gbuffer.normals[ni + 2] = nz;
        }
      }
    }

    await renderCapturePass(
      renderer,
      scene,
      camera,
      res.depthRenderTarget,
      1,
      hiddenState.captureMeshes,
      makeDepthMaterial,
    );
    for (const tile of tiles) {
      const depthBytes = (await readRenderTargetTile(
        renderer,
        res.depthRenderTarget,
        tile,
      )) as Float32Array;
      for (let row = 0; row < tile.height; row++) {
        const tileRowOffset = row * tile.width;
        const dstRowOffset = (tile.y + row) * width + tile.x;
        for (let col = 0; col < tile.width; col++) {
          const tileIndex = tileRowOffset + col;
          const dstIndex = dstRowOffset + col;
          if (gbuffer.objectIds[dstIndex] === 0) continue;
          const bi = tileIndex * 4;
          gbuffer.depth[dstIndex] = normalizedDepthToLinear(
            camera,
            depthBytes[bi],
          );
        }
      }
    }

    console.log(
      `[Inking:readback] ${width}×${height}: ${geometryCount} geometry px, ` +
        `${pixelCount - geometryCount} background px`,
    );

    const out: GBufferData = {
      width,
      height,
      depth: gbuffer.depth,
      normals: gbuffer.normals,
      objectIds: gbuffer.objectIds,
      luminance: gbuffer.luminance,
      resolvedAutoSmoothAngles: gbuffer.resolvedAutoSmoothAngles,
    };
    returned = true;
    return out;
  } finally {
    if (!returned) recycleGBuffer(gbuffer);
    scene.background = savedBg;
    restoreSceneVisibility(hiddenState.hidden);
    RendererUtils.restoreRendererState(renderer as any, res.savedState);
    renderer.setRenderTarget(null);
  }
}

export function disposeReadbackTargets(): void {
  disposeReusableGBufferPool();
  if (!resources) return;

  resources.colorRenderTarget.dispose();
  resources.depthRenderTarget.dispose();
  resources = null;
}
